import React from "react";
import { motion } from "framer-motion";
import {
  Code2,
  Server,
  ShieldCheck,
  Zap,
  Cpu,
  Cloud,
  Rocket,
  ArrowRight,
  CheckCircle2,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";

// --- Lightweight local UI primitives (no external UI library) ---
const Btn = ({ className = "", children, ...props }) => (
  <button
    className={`inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Card = ({ className = "", children }) => (
  <div className={`rounded-2xl border ${className}`}>{children}</div>
);
const CardHeader = ({ children, className = "" }) => (
  <div className={`p-6 border-b ${className}`}>{children}</div>
);
const CardTitle = ({ children, className = "" }) => (
  <h3 className={`text-lg font-semibold ${className}`}>{children}</h3>
);
const CardContent = ({ children, className = "" }) => (
  <div className={`p-6 ${className}`}>{children}</div>
);

const Input = ({ className = "", ...props }) => (
  <input
    className={`w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${className}`}
    {...props}
  />
);
const Textarea = ({ className = "", ...props }) => (
  <textarea
    className={`w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${className}`}
    {...props}
  />
);
// ---------------------------------------------------------------

// Helper: smooth scroll to section by id
const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Reusable animation presets
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.6, ease: "easeOut" },
  }),
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
};

// Brand primitives
const Brand = {
  name: "Platra Solutions",
  tagline: "Ship faster. Scale safer.",
  primary: "#e93a20", // primary
  secondary: "#2a313e", // secondary
};

export default function PlatraLanding() {
  const [loading, setLoading] = React.useState(false);
  const [showMessage, setShowMessage] = React.useState(false);

  return (
    <div className="min-h-screen bg-white text-[#2a313e] selection:bg-[#e93a20]/20">
      {/* Navbar */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-[#2a313e]/10">
        <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div onClick={() => scrollToId("hero")} className="flex items-center gap-2 cursor-pointer">
            <img className="w-[125px]" src="logo.jpg" alt="P" />
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm text-[#2a313e]/70">
            {[
              ["Services", "services"],
              ["Solutions", "solutions"],
              ["Process", "process"],
              ["About", "about"],
              ["Contact", "contact"],
            ].map(([label, id]) => (
              <button
                key={id}
                onClick={() => scrollToId(id)}
                className="hover:text-[#2a313e] transition-colors"
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Btn onClick={() => scrollToId("contact")} className="gap-2 bg-[#e93a20] hover:bg-[#cf331c] text-white focus:ring-[#e93a20]">
              Get a quote <ArrowRight className="h-4 w-4" />
            </Btn>
          </div>
        </nav>
      </header>

      {/* Hero */}
      <section id="hero" className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 md:py-28">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <motion.h1
                variants={fadeUp}
                className="text-4xl md:text-6xl font-extrabold tracking-tight"
              >
                Engineering modern software that <span className="text-[#e93a20]">moves your business</span>
              </motion.h1>
              <motion.p
                custom={1}
                variants={fadeUp}
                className="mt-5 text-lg text-[#2a313e]/70"
              >
                {Brand.tagline} — from MVPs to planet‑scale platforms. We design, build, and run software so you can focus on growth.
              </motion.p>
              <motion.div custom={2} variants={fadeUp} className="mt-8 flex flex-wrap gap-3">
                <Btn size="lg" onClick={() => scrollToId("contact")} className="gap-2 bg-[#e93a20] hover:bg-[#cf331c] text-white focus:ring-[#e93a20]">
                  Start a project <Rocket className="h-4 w-4" />
                </Btn>
                <Btn onClick={() => scrollToId("services")} className="border border-[#2a313e]/20 text-[#2a313e] hover:bg-[#2a313e]/5 focus:ring-[#2a313e] bg-white">
                  Explore services
                </Btn>
              </motion.div>
              <motion.div custom={3} variants={fadeUp} className="mt-8 flex items-center gap-4 text-[#2a313e]/60 text-sm">
                <div className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> ISO‑style delivery</div>
                <div className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Cloud‑native</div>
                <div className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Security first</div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <Card className="relative bg-white border-[#2a313e]/10 shadow-sm">
                <CardHeader className="border-[#2a313e]/10">
                  <CardTitle className="flex items-center gap-2">
                    <Cpu className="h-5 w-5" /> Studio snapshot
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    {[
                      {
                        label: "Founder experience",
                        value: "6+ yrs",
                        Icon: Code2,
                      },
                      {
                        label: "Active projects",
                        value: "3",
                        Icon: Rocket,
                      },
                      {
                        label: "Avg. response",
                        value: "< 24h",
                        Icon: Mail,
                      },
                    ].map(({ label, value, Icon }, i) => (
                      <motion.div
                        key={label}
                        variants={scaleIn}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: i * 0.1 }}
                        className="rounded-2xl border border-[#2a313e]/10 p-4 bg-white"
                      >
                        <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-[#2a313e]/10">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="text-2xl font-bold">{value}</div>
                        <div className="text-xs text-[#2a313e]/60">{label}</div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="mt-6 h-2 w-full overflow-hidden rounded-full bg-[#2a313e]/10">
                    <motion.div
                      className="h-full"
                      style={{ backgroundColor: Brand.primary }}
                      initial={{ width: 0 }}
                      animate={{ width: "40%" }} // we keep some capacity free
                      transition={{ duration: 1.2, ease: "easeOut", delay: 0.2 }}
                    />
                  </div>
                  <div className="mt-2 text-right text-xs text-[#2a313e]/60">
                    Capacity filled with current clients
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-bold"
          >
            End‑to‑end services
          </motion.h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Custom App Development",
                desc: "Full‑stack web & mobile apps with modern DX.",
                icon: Code2,
                bullets: ["React / Next.js", "Node / Python", "iOS & Android"],
              },
              {
                title: "Cloud & DevOps",
                desc: "CI/CD, IaC, observability and SRE best practices.",
                icon: Cloud,
                bullets: ["AWS / Azure / GCP", "Kubernetes & Docker", "Terraform"],
              },
              {
                title: "Security & Compliance",
                desc: "Shift‑left security, audits, and hardening.",
                icon: ShieldCheck,
                bullets: ["OWASP ASVS", "SOC2 readiness", "Zero trust"],
              },
            ].map((s, i) => (
              <motion.div
                key={s.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
              >
                <Card className="h-full bg-white border-[#2a313e]/10">
                  <CardHeader className="border-[#2a313e]/10">
                    <CardTitle className="flex items-center gap-2">
                      <s.icon className="h-5 w-5" /> {s.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-[#2a313e]/70">
                    <p>{s.desc}</p>
                    <ul className="mt-4 space-y-2 text-sm">
                      {s.bullets.map((b) => (
                        <li key={b} className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-[#e93a20]" /> {b}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="py-20 border-y border-[#2a313e]/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-bold"
          >
            Solution accelerators
          </motion.h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Composable Commerce",
                desc: "Headless storefronts with performant edge rendering.",
                icon: Zap,
              },
              {
                title: "Data Platforms",
                desc: "Streaming + warehouse stacks for near‑real‑time insight.",
                icon: Server,
              },
              {
                title: "GenAI Integrations",
                desc: "LLM‑powered copilots, RAG search, and workflow agents.",
                icon: Cpu,
              },
            ].map((s, i) => (
              <motion.div key={s.title} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}>
                <Card className="h-full bg-white border-[#2a313e]/10">
                  <CardHeader className="border-[#2a313e]/10">
                    <CardTitle className="flex items-center gap-2">
                      <s.icon className="h-5 w-5 text-[#e93a20]" /> {s.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-[#2a313e]/70">{s.desc}</CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="process" className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-bold"
          >
            A pragmatic delivery process
          </motion.h2>
          <div className="mt-10 grid md:grid-cols-4 gap-6">
            {[
              { step: "Discover", text: "Clarify goals, scope, and constraints." },
              { step: "Design", text: "Architecture & UX with security in mind." },
              { step: "Deliver", text: "Agile sprints, CI/CD, quality gates." },
              { step: "Scale", text: "Observability, SRE, and optimization." },
            ].map((p, i) => (
              <motion.div
                key={p.step}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="rounded-2xl border border-[#2a313e]/10 p-6 bg-white"
              >
                <div className="inline-flex items-center justify-center rounded-xl px-3 py-1 text-xs font-semibold text-white" style={{ backgroundColor: Brand.primary }}>{p.step}</div>
                <p className="mt-3 text-[#2a313e]/70">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-20 border-y border-[#2a313e]/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-bold"
          >
            Why Platra?
          </motion.h2>
          <div className="mt-8 grid md:grid-cols-2 gap-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <p className="text-[#2a313e]/80 text-lg">
                We’re a software solutions partner focused on business outcomes. Our teams combine strong architecture with product sense to deliver high‑impact software quickly and safely.
              </p>
              <ul className="mt-6 space-y-3 text-[#2a313e]/80">
                {[
                  "Senior‑only engineering pods",
                  "Security & privacy by default",
                  "Transparent delivery metrics",
                  "Cloud cost awareness",
                ].map((i) => (
                  <li key={i} className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-[#e93a20]" /> {i}</li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <Card className="bg-white border-[#2a313e]/10 h-full">
                <CardHeader className="border-[#2a313e]/10">
                  <CardTitle>Tech we love</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3 text-[#2a313e]/70">
                  {[
                    "React / Next.js",
                    "Node.js / NestJS",
                    "Python / FastAPI",
                    "Postgres / Redis",
                    "AWS / Azure / GCP",
                    "Kubernetes / Docker",
                  ].map((t) => (
                    <div key={t} className="rounded-xl border border-[#2a313e]/10 p-3 text-sm">{t}</div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-bold"
          >
            Let’s build something great
          </motion.h2>
          <div className="mt-8 grid md:grid-cols-3 gap-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="md:col-span-2">
              <Card className="bg-white border-[#2a313e]/10">
                <CardContent className="p-6">
                  {showMessage && <div className="mb-3">
                    <div className="p-2 rounded text-sm bg-[#67C23A]/10 text-[#67C23A]">Thank you for your message!</div>
                  </div>}
                  <form className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#2a313e]/70">Full name</label>
                      <Input placeholder="Jane Doe" className="mt-1 bg-white border-[#2a313e]/20 focus:ring-[#e93a20] focus:border-[#e93a20]" />
                    </div>
                    <div>
                      <label className="text-sm text-[#2a313e]/70">Email</label>
                      <Input type="email" placeholder="jane@company.com" className="mt-1 bg-white border-[#2a313e]/20 focus:ring-[#e93a20] focus:border-[#e93a20]" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm text-[#2a313e]/70">Project details</label>
                      <Textarea placeholder="Tell us about your goals, timeline, and budget…" className="mt-1 bg-white border-[#2a313e]/20 min-h-[120px] focus:ring-[#e93a20] focus:border-[#e93a20]" />
                    </div>
                    <div className="md:col-span-2 flex items-center justify-between gap-4">
                      <div className="text-xs text-[#2a313e]/60">We’ll get back to you within 1–2 business days.</div>
                      <Btn
                        disabled={loading}
                        onClick={(e) => {
                          e.preventDefault();
                          setLoading(true);

                          // Simulate sending message (1 sec)
                          setTimeout(() => {
                            setLoading(false);
                            setShowMessage(true);
                          }, 1000);
                        }}
                        className={`gap-2 bg-[#e93a20] hover:bg-[#cf331c] text-white focus:ring-[#e93a20] ${loading ? "opacity-70 cursor-not-allowed" : ""}`}
                      >
                        {loading ? (
                          <div className="flex items-center gap-2">
                            <span className="loader h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Sending...
                          </div>
                        ) : (
                          <>
                            Send message <ArrowRight className="h-4 w-4" />
                          </>
                        )}
                      </Btn>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-xl bg-[#2a313e]/10 p-2"><Phone className="h-5 w-5" /></div>
                  <div>
                    <div className="text-sm text-[#2a313e]/60">Phone</div>
                    <div className="font-medium">+92 330 0288971</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="rounded-xl bg-[#2a313e]/10 p-2"><Mail className="h-5 w-5" /></div>
                  <div>
                    <div className="text-sm text-[#2a313e]/60">Email</div>
                    <div className="font-medium">info@platrasolutions.com</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="rounded-xl bg-[#2a313e]/10 p-2"><MapPin className="h-5 w-5" /></div>
                  <div>
                    <div className="text-sm text-[#2a313e]/60">HQ</div>
                    <div className="font-medium">Remote‑first • Pakistan</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#2a313e]/10 py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-[#2a313e]/60">
          <div className="flex items-center gap-2">
            <img className="w-[80px]" src="vertical.jpg" alt="P" />
            <span className="hidden md:inline">•</span>
            <span>© {new Date().getFullYear()} All rights reserved.</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => scrollToId("hero")} className="hover:text-[#2a313e]">Back to top</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
